import Footer from "../../components/footer";
import Header from "../../components/header";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { Trash2 } from "lucide-react";
import { useMediaQuery } from "react-responsive";
import MobileHeader from "../../components/MobileHeader";

const UPLOAD_OPTIONS = [
  {
    value: "both",
    title: "Add both a public and private file",
    description:
      "Upload a public file which everyone can access, and save a private copy of the same file as a backup which only you and the co-authors can access.",
  },
  {
    value: "public",
    title: "Add only a public file",
    description: "Upload a public file which everyone can access and read.",
  },
  {
    value: "private",
    title: "Add only a private file",
    description:
      "Save a private file as a backup which only you and the co-authors can access.",
  },
];

export default function UploadResearch() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [step, setStep] = useState(1);
  const [uploadType, setUploadType] = useState("");
  const [isChecked, setIsChecked] = useState(false);
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type") || "journal";

  const handleSkip = () => {
    localStorage.removeItem("uploadedResearchFile");
    navigate(`/publish/${type}`);
  };
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    setStep(2);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setUploadType("");
    setIsChecked(false);
    setStep(1);
  };

  const handleUpload = () => {
    if (!selectedFile) return;

    navigate(`/publish/${type}`, {
      state: { uploadedFile: selectedFile },
    });
  };

  const fileSizeKB = selectedFile
    ? Math.round(selectedFile.size / 1024).toLocaleString()
    : 0;

  return (
   <div>
  {isMobile ? (
    <MobileHeader hideSearchBar={true} />
  ) : (
    <Header hideSearchBar={true} />
  )}
  
  <div className="min-h-screen flex items-center justify-center bg-gray-50 py-16">
    <div className="w-full max-w-2xl mx-auto px-4">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-[#e8f0f7] rounded-2xl flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-[#0f6abf]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Add research to your profile</h2>
        <p className="text-sm text-gray-500 mt-2">Share your work with the community</p>
      </div>

      {/* Step 1: Upload File */}
      {step === 1 && (
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Do you want to add a file?</h3>

          <label className="inline-flex flex-col items-center cursor-pointer">
            <div className="w-32 h-32 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center hover:border-[#0f6abf] transition-colors">
              <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <span className="text-sm text-gray-600">Select and upload file</span>
            </div>
            <input type="file" hidden onChange={handleFileUpload} />
          </label>

          <div className="mt-8 pt-6 border-t border-gray-100">
            <p className="text-sm text-gray-500 mb-4">You can add details about this research in the next step</p>
            <button 
              className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
              onClick={handleSkip}
            >
              Skip
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Upload Details */}
      {step === 2 && (
        <div className="bg-white rounded-2xl shadow-lg p-8">
          {/* File Info */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl mb-6">
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <div>
                <p className="font-medium text-gray-900">{selectedFile?.name}</p>
                <p className="text-xs text-gray-500">{fileSizeKB} KB</p>
              </div>
            </div>
            <button 
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              onClick={handleRemoveFile}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Remove
            </button>
          </div>

          <h3 className="text-lg font-semibold text-gray-900 mb-6">Select how you want to upload your file</h3>

          {/* Upload Options */}
          <div className="space-y-3 mb-6">
            {UPLOAD_OPTIONS.map((opt) => (
              <label key={opt.value} className={`flex items-start gap-4 p-4 border rounded-xl cursor-pointer transition-all ${
                uploadType === opt.value 
                  ? "border-[#0f6abf] bg-[#e8f0f7]/20" 
                  : "border-gray-200 hover:border-gray-300"
              }`}>
                <input
                  type="radio"
                  name="uploadType"
                  value={opt.value}
                  checked={uploadType === opt.value}
                  onChange={(e) => setUploadType(e.target.value)}
                  className="mt-0.5 w-4 h-4 text-[#0f6abf] focus:ring-[#0f6abf]"
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{opt.title}</div>
                  <div className="text-sm text-gray-500">{opt.description}</div>
                </div>
              </label>
            ))}
          </div>

          {/* Verify Checkbox */}
          <div className="mb-6 p-4 bg-gray-50 rounded-xl">
            <label className="flex gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={isChecked}
                onChange={(e) => setIsChecked(e.target.checked)}
                className="mt-0.5 w-4 h-4 text-[#0f6abf] focus:ring-[#0f6abf] rounded"
              />
              <span className="text-sm text-gray-700 leading-relaxed">
                I have reviewed and verified each file I am uploading. I have the right to share each file publicly and/or store a private copy accessible to me and the co-authors, as applicable. By uploading this file, I agree to the Upload Conditions.
              </span>
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <button 
              className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
              onClick={handleSkip}
            >
              Skip
            </button>
            <button
              className={`px-6 py-2.5 rounded-xl font-medium transition-colors ${
                !isChecked || !uploadType
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-[#0f6abf] text-white hover:bg-[#0a5ca8]"
              }`}
              onClick={handleUpload}
              disabled={!isChecked || !uploadType}
            >
              Upload
            </button>
          </div>
        </div>
      )}
    </div>
  </div>

  <Footer />
</div>
  );
}
